export function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Capitalizes the first letter of a string.
 * @param str The string to capitalize.
 * @returns The capitalized string.
 */
function capitalize(str: string): string {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Converts a string to camelCase.
 * @param str The string to convert.
 * @returns The camelCased string.
 */
export function camelCase(str: string): string {
  return str
    .replace(/[-_\s]+(.)?/g, (_, c) => (c ? c.toUpperCase() : ''))
    .replace(/^(.)/, (m) => m.toLowerCase());
}

/**
 * Converts a string to snake_case.
 * @param str The string to convert.
 * @returns The snake_cased string.
 */
export function snakeCase(str: string): string {
  return str
    .trim()
    .replace(/[-\s]+/g, '_')
    .replace(/([A-Z])/g, '_$1')
    .toLowerCase()
    .replace(/^_+/, '');
}

/**
 * Converts a string to kebab-case.
 * @param str The string to convert.
 * @returns The kebab-cased string.
 */
export function kebabCase(str: string): string {
  return str
    .trim()
    .replace(/[-_\s]+/g, '-')
    .replace(/([A-Z])/g, '-$1')
    .toLowerCase()
    .replace(/^-+/, '');
}

/**
 * Converts a string to Title Case.
 * @param str The string to convert.
 * @returns The title cased string.
 */
export function titleCase(str: string): string {
  return str
    .toLowerCase()
    .split(/\s+/)
    .map((word) => capitalize(word))
    .join(' ');
}

/**
 * Truncates a string to a specified length and appends an ellipsis if it exceeds that length.
 * @param str The string to truncate.
 * @param length The maximum length of the string.
 * @param end The string to append at the end if truncation occurs (default is '...').
 * @returns The truncated string.
 */
export function truncate(str: string, length: number, end = '...'): string {
  if (str.length <= length) return str;
  return str.slice(0, length - end.length) + end;
}

/**
 * Removes all whitespace from a string.
 * @param str The string to process.
 * @returns The string with all whitespace removed.
 */
export function removeSpace(str: string): string {
  return str.replace(/\s+/g, '');
}

/**
 * Checks if a string is a valid email address.
 * @param str The string to check.
 * @returns True if the string is a valid email address, false otherwise.
 */
export function isEmail(str: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(str);
}

/**
 * Checks if a string is a valid Australian phone number.
 * @param str The string to check.
 * @returns True if the string is a valid Australian phone number, false otherwise.
 */
export function isAustralianPhoneNumber(str: string): boolean {
  const australianPhoneRegex = /^04[0-9]{8}$/;
  return australianPhoneRegex.test(str);
}
